import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api';

const pad = (n) => String(n).padStart(2, '0');
const toDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today = () => toDate(new Date());
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toDate(d); };
const num = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 }));
const rupees = (n) => (n == null ? '—' : `${n < 0 ? '−' : n > 0 ? '+' : ''}₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`);
const niceDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const niceTime = (utc) => (utc ? new Date(`${utc.replace(' ', 'T')}Z`).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '');

const S = {
  root: { fontFamily: "'Manrope', system-ui, sans-serif", color: '#231a33', padding: '16px 20px 40px' },
  h1: { fontFamily: "'Bricolage Grotesque','Manrope',sans-serif", fontSize: 26, margin: 0 },
  muted: { color: '#776e86', fontSize: 13 },
  bar: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '14px 0' },
  input: { height: 40, padding: '0 10px', border: '1px solid #ece6f2', borderRadius: 10, background: '#fff', font: 'inherit', fontSize: 14 },
  btn: { height: 40, padding: '0 14px', border: '1px solid #ece6f2', borderRadius: 10, background: '#fff', font: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  cards: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
  card: { flex: '1 1 160px', padding: '10px 14px', border: '1px solid #ece6f2', borderRadius: 14, background: '#fff' },
  cardLabel: { display: 'block', fontSize: 12, color: '#776e86' },
  cardVal: { fontSize: 20, fontWeight: 800 },
  wrap: { overflowX: 'auto', border: '1px solid #ece6f2', borderRadius: 16, background: '#fff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { background: '#faf8fc', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#776e86', padding: '10px 12px', borderBottom: '1px solid #ece6f2', whiteSpace: 'nowrap' },
  td: { padding: '9px 12px', borderBottom: '1px solid #f4f0f8', verticalAlign: 'middle' },
  num: { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  dateRow: { background: '#f3eff8', fontWeight: 800, color: '#5b3fd9', fontSize: 13 },
};

export default function CountHistoryPage() {
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(today());
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    const qs = new URLSearchParams({ from, to, type, q }).toString();
    api.get(`/count-history?${qs}`).then(setData).catch((e) => setError(e.message));
  }, [from, to, type, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const rows = data ? data.rows : [];
  let lastDate = null;

  return (
    <div style={S.root} className="ch-root">
      <style>{`@media print { body * { visibility: hidden !important; } .ch-root, .ch-root * { visibility: visible !important; } .ch-root { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; } .ch-noprint { display: none !important; } .ch-root table { font-size: 11px !important; } .ch-root td, .ch-root th { padding: 4px 6px !important; } }`}</style>
      <h1 style={S.h1}>Count history</h1>
      <div style={S.muted}>Every stock correction from Available Stock counts and shift closes: what the system had, what was counted, and the difference.</div>

      <div style={S.bar} className="ch-noprint">
        <label style={S.muted}>From <input type="date" style={S.input} value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
        <label style={S.muted}>To <input type="date" style={S.input} value={to} min={from} max={today()} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
        <select style={S.input} value={type} onChange={(e) => setType(e.target.value)}>
          <option value="all">All counts</option>
          <option value="manual">Available Stock counts</option>
          <option value="shift">Shift close counts</option>
        </select>
        <input style={{ ...S.input, flex: '1 1 200px' }} placeholder="Search item" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" style={S.btn} onClick={() => window.print()}>Print</button>
      </div>

      {error && <div style={{ padding: 12, borderRadius: 12, background: '#fdecef', color: '#d63d5b', fontWeight: 700, marginBottom: 12 }}>{error}</div>}

      {data && (
        <div style={S.cards}>
          <div style={S.card}><span style={S.cardLabel}>Changes</span><strong style={S.cardVal}>{data.summary.entries}</strong></div>
          <div style={S.card}><span style={S.cardLabel}>Short ({data.summary.short_items} items)</span><strong style={{ ...S.cardVal, color: '#d63d5b' }}>{rupees(data.summary.short_value)}</strong></div>
          <div style={S.card}><span style={S.cardLabel}>Extra ({data.summary.extra_items} items)</span><strong style={{ ...S.cardVal, color: '#b86a00' }}>{rupees(data.summary.extra_value)}</strong></div>
          {data.summary.no_cost > 0 && <div style={S.card}><span style={S.cardLabel}>Items with no purchase cost</span><strong style={{ ...S.cardVal, color: '#776e86' }}>{data.summary.no_cost}</strong></div>}
        </div>
      )}

      <div style={{ ...S.muted, fontWeight: 800, margin: '0 2px 8px', display: 'none' }} className="ch-print-title">Count history {from} to {to}</div>

      <div style={S.wrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Time</th>
              <th style={S.th}>Item</th>
              <th style={{ ...S.th, ...S.num }}>System</th>
              <th style={{ ...S.th, ...S.num }}>Counted</th>
              <th style={{ ...S.th, ...S.num }}>Difference</th>
              <th style={{ ...S.th, ...S.num }}>Value</th>
              <th style={S.th}>Type</th>
              <th style={S.th}>By</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const header = r.date !== lastDate;
              lastDate = r.date;
              return (
                <React.Fragment key={r.id}>
                  {header && <tr><td colSpan={8} style={{ ...S.td, ...S.dateRow }}>{niceDate(r.date)}</td></tr>}
                  <tr>
                    <td style={{ ...S.td, color: '#776e86', whiteSpace: 'nowrap' }}>{niceTime(r.created_at) || '—'}</td>
                    <td style={S.td}><strong>{r.name}</strong><div style={{ fontSize: 12, color: '#776e86' }}>{r.category}, {r.unit}</div></td>
                    <td style={{ ...S.td, ...S.num }}>{num(r.system)}</td>
                    <td style={{ ...S.td, ...S.num }}>{num(r.counted)}</td>
                    <td style={{ ...S.td, ...S.num, fontWeight: 800, color: r.change < 0 ? '#d63d5b' : r.change > 0 ? '#b86a00' : '#231a33' }}>{r.change > 0 ? '+' : ''}{num(r.change)}</td>
                    <td style={{ ...S.td, ...S.num, color: r.value < 0 ? '#d63d5b' : '#231a33' }}>{rupees(r.value)}</td>
                    <td style={S.td}>{r.kind}<div style={{ fontSize: 12, color: '#776e86' }}>{r.label}</div></td>
                    <td style={S.td}>{r.by || '—'}</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {data && rows.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#776e86' }}>No count changes in this period.</div>}
      </div>
    </div>
  );
}
